// useRecentColors — Modern スコープの「最近色」記録フック (段階1、consult 2026-06-12).
//
// `chrome.pen.color` (= settle した描画色) の変化を購読し、最近色リストを most-recent-first /
// dedupe / 最大 RECENT_COLORS_MAX で保持する。畳み込みは純関数 `pushRecentColor`、本フックは
// **記録タイミング (= settle 検出)** だけを担う。
//
// ## 中間ドラッグ色で溢れさせない (= デバウンス、consult 2026-06-12)
//   ModernColorPanel の `commitColor` はカラーホイール drag の onChange ごとに発火するため、
//   `chrome.pen.color` は drag 中 60fps で連続変化する。これを生で記録すると最近色行が中間色で
//   溢れる。そこで **色が DEBOUNCE_MS だけ変化せず落ち着いたら** 記録する (= 操作が settle した
//   値だけ最近色に入る)。drag を止めた最終色のみが拾われ、途中の通過色は記録されない。
//
// ## Modern スコープ完結 (= blast radius 最小化)
//   共有 `useRoomChrome` には手を入れない (Classic は最近色を出さない)。ModernLayout がこの
//   フックを持ち、`chrome.pen.color` を渡す。記録対象は「色」だけ (alpha/pen 種は無関係)。

import { useCallback, useEffect, useRef, useState } from 'react';

import { pushRecentColor } from './recentColors.js';

/** 色が「落ち着いた」と判定するまでの猶予 (ms)。drag の通過色を弾く一方、手早い
 *  色切替でも体感遅延が無い程度の短さ。 */
const DEBOUNCE_MS = 400;

/** 色変化を受けて取るべき処置 (= 純判定の結果。effect / テストが共有する)。 */
export type RecordDecision =
  /** 直近記録と同色 → 何もしない (= 並び不変、debounce タイマーも張らない)。 */
  | { readonly kind: 'noop' }
  /** 抑制対象色の再利用 → 記録せず既知扱い (lastRecorded を更新し抑制解除)、並び不変。 */
  | { readonly kind: 'suppress' }
  /** 新規 settle → DEBOUNCE_MS 後に pushRecentColor で記録 (stale 抑制は解除済)。 */
  | { readonly kind: 'schedule' };

/** 色変化判定の入力 state (= 現在色 + ref 値)。すべて rgb24 正規化済の前提。 */
export interface RecordInputs {
  /** 正規化済の新しい色 (= color & 0xffffff)。 */
  readonly color: number;
  /** 直近に記録 (or 既知扱い) した色。未記録なら null。 */
  readonly lastRecorded: number | null;
  /** 抑制対象色。なければ null。 */
  readonly suppress: number | null;
}

/**
 * 色変化に対する処置を決める純関数 (= effect の本体ロジックを切り出してテスト可能に)。
 * 副作用は持たず、呼び出し側 (effect) が結果に応じて ref/state を更新する。優先順位:
 *   1. 直近記録と同色 → noop (= 並び不変)。
 *   2. 抑制対象と同色 → suppress (= 既存色の再利用、記録しない・並び不変)。
 *   3. それ以外 → schedule (= 新規 settle、debounce 後に記録。stale 抑制は呼び出し側で解除)。
 */
export const decideRecordAction = ({
  color,
  lastRecorded,
  suppress,
}: RecordInputs): RecordDecision => {
  if (lastRecorded === color) return { kind: 'noop' };
  if (suppress === color) return { kind: 'suppress' };
  return { kind: 'schedule' };
};

export interface UseRecentColorsResult {
  /** 最近色 (rgb24、most-recent-first、最大 RECENT_COLORS_MAX)。空配列スタート。 */
  readonly recentColors: ReadonlyArray<number>;
  /**
   * 次に settle する `color` (= rgb24) を「既存色の再利用」として **記録しない**よう抑制する
   * (consult 2026-06-12)。最近色クリック / スロット選択は `chrome.onColorChange` /
   * `onPaletteSlotSelect` 経由で pen.color を変えるが、これは「新しく作った色」ではなく
   * 既存色の読込なので最近色リストに積み直さない (= 並びも変えない)。呼び出し側が
   * 「色変更を発火する直前」にその色を渡して抑制する。
   *
   * 抑制は **1 回限り**: 抑制対象と一致する色が次に settle したら記録せず既知扱いにし、
   * 抑制を解除する。別色が settle した場合は stale な抑制を解除する (= クリック直後に
   * ホイールで別色へ編集した新色は通常どおり記録される)。
   */
  readonly suppressNextRecord: (color: number) => void;
  /**
   * 明示的に `color` (= rgb24) を 1 回だけ最近色へ記録する (2026-06-12 スポイト厳密化)。
   *
   * デバウンス経路と独立に、呼び出し側が「確定した1色」を即記録するための口。スポイトの
   * 確定 (= pointerup) でこれを呼ぶ。記録後は `lastRecorded` を更新するので、直後に
   * 同色がデバウンス経路へ流れても二重記録されない (= noop に倒れる)。`suspended` 中の
   * 抑止と組み合わせて「スポイトはドラッグ通過色を弾き、確定の1色だけ入る」を実現する。
   *
   * 直近記録と同色なら何もしない (= 並び不変)。stale な `suppressNextRecord` 抑制も解除する
   * (= 明示記録は確定操作なので、保留していた抑制は無効化して整合させる)。
   */
  readonly recordColor: (color: number) => void;
}

/** `useRecentColors` のオプション (2026-06-12 スポイト厳密化)。 */
export interface UseRecentColorsOptions {
  /**
   * `true` の間、`color` 変化に対する **自動デバウンス記録を一切張らない** (= スポイト操作中)。
   *
   * スポイトはドラッグ中 `chrome.pen.color` を連続変化させるが、その通過色・静止色は
   * 最近色に入れたくない (確定 = pointerup の1色だけ入れる)。suspend 中は schedule を
   * 立てず、確定時に呼び出し側が `recordColor` で明示記録する。
   *
   * **非スポイト経路 (カラーホイール編集等) には立てない**こと: それらは従来どおりデバウンス
   * 記録される (= この機能はスポイト由来の記録だけを差し替える)。
   */
  readonly suspended?: boolean;
}

/**
 * `color` (= chrome.pen.color, rgb24) の settle を購読して最近色履歴を返す。
 * 起動直後は空配列 (= 履歴ゼロ)。表示側 (ModernColorPalette) が空状態を綺麗に扱う。
 *
 * `options.suspended` が `true` の間は自動デバウンス記録を張らず、`recordColor` で
 * 明示記録した1色だけが入る (= スポイト確定の厳密化、2026-06-12)。
 */
export const useRecentColors = (
  color: number,
  options?: UseRecentColorsOptions,
): UseRecentColorsResult => {
  const suspended = options?.suspended ?? false;
  const [recentColors, setRecentColors] = useState<ReadonlyArray<number>>([]);

  // 直近に記録した色。同じ色が settle で再到達しても無駄な setState を避ける番兵。
  const lastRecordedRef = useRef<number | null>(null);

  // 抑制対象色 (rgb24)。最近色クリック / スロット選択で「次に settle する色」が来る前に
  // セットされ、その色の settle を「記録せず既知扱い」にして 1 回で解除する。null = 抑制なし。
  const suppressRef = useRef<number | null>(null);

  const suppressNextRecord = useCallback((c: number): void => {
    suppressRef.current = c & 0xffffff;
  }, []);

  // 明示記録 (= スポイト確定など): デバウンスを経ずに1色だけ即 push する。lastRecorded を
  // 更新するので、直後に同色がデバウンス経路へ流れても二重記録されない (= noop に倒れる)。
  // stale な抑制も解除して整合させる (= 確定操作は抑制より優先)。
  const recordColor = useCallback((c: number): void => {
    const v = c & 0xffffff;
    if (lastRecordedRef.current === v) return; // 直近記録と同色 → 並び不変。
    suppressRef.current = null;
    lastRecordedRef.current = v;
    setRecentColors((list) => pushRecentColor(list, v));
  }, []);

  useEffect(() => {
    // suspended (= スポイト操作中) は自動デバウンス記録を一切張らない。通過色・静止色を
    // 弾き、確定時に呼び出し側が recordColor で明示記録する (2026-06-12 スポイト厳密化)。
    if (suspended) return;
    // color は rgb24 前提だが念のため正規化して比較 (= スウォッチ hex 化と一致)。
    const c = color & 0xffffff;
    const decision = decideRecordAction({
      color: c,
      lastRecorded: lastRecordedRef.current,
      suppress: suppressRef.current,
    });

    // 直近記録と同色 (= drag 再開で先頭が自身に繰り上がる無駄)。何もしない (並び不変)。
    if (decision.kind === 'noop') return;

    // 抑制対象色の再利用 (= 最近色クリック / スロット選択由来)。既知扱いにして抑制解除、
    // 最近色リストは setState しない (= 並び不変)。直後 drag 再開でも繰り上げない。
    if (decision.kind === 'suppress') {
      suppressRef.current = null;
      lastRecordedRef.current = c;
      return;
    }

    // schedule: 新規 settle。stale 抑制を解除 (= クリック直後にホイールで別色編集した
    // 新色は通常どおり記録される)。DEBOUNCE_MS だけ変化しなければ記録する。drag 中は
    // color が連続更新 → effect 再実行で前回 timer が cleanup され、中間色では発火しない。
    suppressRef.current = null;
    const timer = setTimeout(() => {
      lastRecordedRef.current = c;
      setRecentColors((list) => pushRecentColor(list, c));
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [color, suspended]);

  return { recentColors, suppressNextRecord, recordColor };
};
